import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import DateRangeCalendar from '../../components/DateRangeCalendar';
import Select from '../../components/Select';
import Toggle from '../../components/Toggle';
import { Segmented, usePersisted } from './charts';
import OverviewTab from './OverviewTab';
import ChannelsTab from './ChannelsTab';
import ProductsTab from './ProductsTab';
import CustomersTab from './CustomersTab';
import MarketingTab from './MarketingTab';
import '../../styles/Analytics.css';

// Аналитика v3 (12.10.2026): дашборды владельца «Сводка / Финансы / Товары / Клиенты / Маркетинг» —
// как в обычной корпоративной аналитике. Какие блоки показывать — решает сервер по подключённым
// модулям (площадки, вертикаль, реклама): GET /analytics/v3/layout. Настройки — шестерёнка справа.

export const V3_TABS = ['overview', 'channels', 'products', 'customers', 'marketing'];

const GearIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

const PERIODS = [
  { value: 'today', label: 'Сегодня' },
  { value: '7', label: '7 дней' },
  { value: '30', label: '30 дней' },
  { value: '90', label: 'Квартал' },
  { value: '365', label: 'Год' },
];
// «По оплате» убрали (владелец 13.10): от оформления до оплаты не больше часа — почти то же, что «по оформлению»
// По умолчанию — по дню оформления (владелец 13.10)
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');

/** Настройки статистики — отдельная вкладка (одна на все разделы, просьба владельца 12.10) */
function SettingsPage({ showToast }) {
  const [week, setWeek] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.getWeeklySummary().then(setWeek).catch(e => showToast?.(e.message, 'error'));
  }, [showToast]);
  const toggleOwner = async (id, enabled) => {
    try { setWeek(await api.setWeeklySummaryOwner(id, enabled)); } catch (e) { showToast?.(e.message, 'error'); }
  };
  const sendMe = async () => {
    setSending(true);
    try {
      const r = await api.sendWeeklySummaryToMe();
      if (r.sent?.length) showToast?.(`Отправлено вам в ${r.sent[0].channel === 'max' ? 'Max' : 'Telegram'}`);
      else showToast?.(`Не дошло: ${r.failed?.[0]?.reason || 'нет Telegram и Max'}`, 'error');
    } catch (e) {
      showToast?.(e.message, 'error');
    } finally {
      setSending(false);
    }
  };
  return (
    <section className="card an-panel">
      <div className="an-panel-head"><h2 className="an-panel-title">Настройки статистики</h2></div>
      <div className="an-panel-body">
      {!week ? <div className="an-muted">Загрузка…</div> : (
        <div className="an-stack" style={{ gap: 18 }}>
          <div>
            <div className="an-subtitle">Сводка за неделю</div>
            <div className="an-muted an-small" style={{ marginBottom: 8 }}>
              По понедельникам в {week.send_at} — итоги прошлой недели от бота магазина в Telegram, нет Telegram — в Max.
            </div>
            {week.owners.map(o => (
              <Toggle key={o.id} checked={o.enabled} disabled={!o.telegram && !o.max && !o.enabled}
                onChange={(v) => toggleOwner(o.id, v)} label={o.name}
                hint={o.telegram ? 'Telegram' : o.max ? 'Max' : 'нет Telegram и Max — бот не сможет написать'} />
            ))}
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={sending} onClick={sendMe}>
                {sending ? 'Отправляю…' : 'Прислать мне сейчас'}
              </button>
            </div>
            {week.preview && <pre className="an-preview">{week.preview}</pre>}
          </div>
        </div>
      )}
      </div>
    </section>
  );
}

export default function AnalyticsV3({ showToast, onNavigate }) {
  const [layout, setLayout] = useState(null);
  // Раздел, период, день заказа и сравнение помнятся между вкладками и заходами
  const [tab, setTab] = usePersisted('nf_an_tab', 'overview');
  // Вход в «Статистику» — всегда на дашборд: раздел помнится между заходами, и кто ушёл из шестерёнки,
  // в следующий раз попадал сразу в настройки (владелец 25.10). «Прежняя статистика» убрана 25.10
  useEffect(() => { if (tab === 'legacy' || tab === 'settings') setTab('overview'); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const [period, setPeriod] = usePersisted('nf_an_period', '7');
  const [range, setRange] = usePersisted('nf_an_range', { from: '', to: '' });
  // новый ключ: у всех, кто уже заходил, сохранено «по доставке» — по умолчанию теперь «по оформлению»
  // Только «по дню оформления»: «по дню доставки» убран из отчётов (владелец 14.10)
  const basis = 'created';
  const [cmp, setCmp] = usePersisted('nf_an_cmp', 'prev');   // prev | year | off
  // «Маркетинг»: report | links — как «Финансы», вход всегда на отчёт: «Ссылки и QR» не запоминаем (26.10)
  const [mkView, setMkView] = useState('report');
  const [finView, setFinView] = useState('report'); // «Финансы»: report | expenses — по умолчанию отчёт (14.10)
  const [prodMode, setProdMode] = usePersisted('nf_an_prod_mode', 'profit');   // «Товары»: profit | stock
  const compare = cmp !== 'off';
  const [showCal, setShowCal] = useState(false);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    api.getAnalyticsLayout().then(setLayout).catch(e => showToast?.(e.message, 'error'));
  }, [showToast]);

  const load = useCallback(async () => {
    if (!V3_TABS.includes(tab)) return;
    const id = ++reqId.current;
    setLoading(true);
    try {
      const q = range.from ? { from: range.from, to: range.to || range.from, basis } : { period, basis };
      if (cmp === 'year') q.compare = 'year';
      const res = await api.getAnalyticsScreen(tab, q);
      if (id === reqId.current) setData(d => ({ ...d, [tab]: res }));
    } catch (e) {
      if (id === reqId.current) showToast?.(e.message || 'Не загрузилось', 'error');
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [tab, period, range, basis, cmp, showToast]);

  useEffect(() => { load(); }, [load]);
  // Из «Ссылки и QR» в «Отчёт» — заново: там могли добавить расход или ссылку
  const onMkView = (v) => { setMkView(v); if (v === 'report') load(); };
  // Из «Затрат» в «Отчёт» — тоже заново: там подтвердили факт или добавили затрату
  const onFinView = (v) => { setFinView(v); if (v === 'report') load(); };

  const blocks = layout?.tabs.find(t => t.id === tab)?.blocks || [];
  const cur = data[tab];
  const p = cur?.period;

  const subtabs = (layout?.tabs || []).map(t => ({ id: t.id, title: t.title }));
  // Один экран — одна дата (UX 15.10): «Ссылки и QR», «Затраты» (экран месяца) и «Остатки» показывают
  // положение на сегодня — выбор периода прячем, чтобы не путать. «Клиенты» — снова с периодом (27.10):
  // сверху «пришли за период», ниже покупатели на сегодня
  const hideControls = (tab === 'marketing' && !!layout?.modules?.marketing && mkView === 'links')
    || (tab === 'channels' && finView === 'expenses')
    || (tab === 'products' && prodMode === 'stock');
  return (
    <div className="an-root">
      {/* Разделы — один переключатель, шестерёнка — иконкой, как во всей админке (владелец 25.10) */}
      <nav className="an-subtabs" aria-label="Дашборды статистики">
        <div className="seg seg-lg">
          {subtabs.map(t => (
            <button key={t.id} type="button" className={`seg-btn${tab === t.id ? ' on' : ''}`} aria-current={tab === t.id ? 'page' : undefined}
              onClick={() => setTab(t.id)}>{t.title}</button>
          ))}
        </div>
        <span className="an-grow" />
        <button type="button" className={`btn-icon${tab === 'settings' ? ' on' : ''}`} onClick={() => setTab('settings')}
          aria-label="Настройки статистики" title="Настройки статистики"><GearIcon /></button>
      </nav>
      {tab === 'settings' ? <SettingsPage showToast={showToast} /> : (<>
      {!hideControls && (<>
      <div className="an-controls">
        <span className="an-desktop-only">
          <Segmented value={range.from ? '' : period} onChange={(v) => { setPeriod(v); setRange({ from: '', to: '' }); }} options={PERIODS} />
        </span>
        <span className="an-mobile-only an-sel-wrap">
          <Select value={range.from ? 'custom' : period}
            onChange={(v) => { if (v === 'custom') { setShowCal(true); return; } setPeriod(v); setRange({ from: '', to: '' }); }}
            options={[...PERIODS, { value: 'custom', label: range.from ? `${fmtD(range.from)} — ${fmtD(range.to || range.from)}` : 'Свой период…' }]} />
        </span>
        <div style={{ position: 'relative' }}>
          <span className="seg seg-lg an-desktop-only">
            <button type="button" className={`seg-btn${range.from ? ' on' : ''}`} onClick={() => setShowCal(s => !s)}>
              {range.from ? `${fmtD(range.from)} — ${fmtD(range.to || range.from)}` : 'Свой период'}
            </button>
          </span>
          {showCal && (
            <DateRangeCalendar dateFrom={range.from} dateTo={range.to}
              onSelect={(from, to) => { setRange({ from, to }); setShowCal(false); }}
              onClose={() => setShowCal(false)} />
          )}
        </div>
        <span className="an-check">
          <span className="an-muted an-small">Сравнить</span>
          <span className="an-sel-wrap an-sel-cmp">
            <Select value={cmp} onChange={setCmp} options={[
              { value: 'prev', label: `с прошлым периодом${p && cmp === 'prev' ? ` (${p.prev_label})` : ''}` },
              { value: 'year', label: `с прошлым годом${p && cmp === 'year' ? ` (${p.prev_label})` : ''}` },
              { value: 'off', label: 'не сравнивать' },
            ]} />
          </span>
        </span>

      </div>
      {/* Даты периода отдельной строкой — лишние (владелец 15.10): период выбран кнопками выше, даты — в блоках */}
      {compare && cur?.prev_empty && (
        <div className="an-muted an-small an-basis-note">В прошлом периоде заказов не было — сравнивать не с чем.</div>
      )}
      </>)}

      {!cur ? <div className="an-muted" style={{ padding: 24 }}>Загрузка…</div> : (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          {tab === 'overview' && <OverviewTab data={cur} blocks={blocks} compare={compare} onOpenTab={setTab}
            onExpenses={() => { setFinView('expenses'); setTab('channels'); }} />}
          {tab === 'channels' && <ChannelsTab data={cur} blocks={blocks} compare={compare} view={finView} onView={onFinView} showToast={showToast}
            onGoMarketing={() => { setMkView('links'); setTab('marketing'); }} />}
          {tab === 'products' && <ProductsTab data={cur} blocks={blocks} mode={prodMode} onMode={setProdMode} onNavigate={onNavigate} />}
          {tab === 'customers' && <CustomersTab data={cur} blocks={blocks} compare={compare} onNavigate={onNavigate} />}
          {tab === 'marketing' && <MarketingTab data={cur} blocks={blocks} view={mkView} onView={onMkView} showToast={showToast}
            onGoFinance={() => { setFinView('expenses'); setTab('channels'); }} />}
        </div>
      )}
      </>)}
    </div>
  );
}
